import { useState } from "react";
import type { ReactNode } from "react";
import type { GroupJournalFieldState, GroupMember } from "@/hooks/group-journal-utils";
import type { ToggleRequest } from "@/hooks/useGroupJournalForm";

interface ToggleableFieldSectionProps {
  label: string;
  required?: boolean;
  state: GroupJournalFieldState;
  members: GroupMember[];
  placeholder?: string;
  rows?: number;
  onSharedChange: (value: string) => void;
  onPerMemberChange: (memberId: number, value: string) => void;
  onRequestToggle: (targetMode: "shared" | "per-member") => ToggleRequest;
  sectionId?: string;
  /** per-member 모드일 때 각 회원 textarea 아래에 렌더링할 추가 UI */
  renderPerMemberAddon?: (memberId: number) => ReactNode;
}

export function ToggleableFieldSection({
  label,
  required,
  state,
  members,
  placeholder,
  rows = 2,
  onSharedChange,
  onPerMemberChange,
  onRequestToggle,
  sectionId,
  renderPerMemberAddon,
}: ToggleableFieldSectionProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<ToggleRequest | null>(null);

  function handleToggle(targetMode: "shared" | "per-member") {
    if (state.mode === targetMode) return;
    const request = onRequestToggle(targetMode);
    if (request.needsConfirm) {
      setPendingToggle(request);
      setShowConfirm(true);
    } else {
      request.execute();
    }
  }

  function handleConfirmToggle() {
    pendingToggle?.execute();
    setShowConfirm(false);
    setPendingToggle(null);
  }

  function handleCancelToggle() {
    setShowConfirm(false);
    setPendingToggle(null);
  }

  return (
    <div id={sectionId}>
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </h3>
        <div className="flex rounded-lg overflow-hidden border border-gray-300 text-xs">
          <button
            type="button"
            className={`px-2.5 py-1 transition-colors ${
              state.mode === "shared"
                ? "bg-primary-500 text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => handleToggle("shared")}
          >
            공통
          </button>
          <button
            type="button"
            className={`px-2.5 py-1 transition-colors ${
              state.mode === "per-member"
                ? "bg-primary-500 text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => handleToggle("per-member")}
          >
            회원별
          </button>
        </div>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
          <p className="text-amber-800 mb-2">
            회원별로 작성된 내용이 다릅니다. 공통으로 전환하면 첫 번째 회원의 내용만 유지됩니다.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1 bg-amber-500 text-white rounded text-xs font-medium hover:bg-amber-600"
              onClick={handleConfirmToggle}
            >
              전환
            </button>
            <button
              type="button"
              className="px-3 py-1 bg-white text-gray-600 border border-gray-300 rounded text-xs font-medium hover:bg-gray-50"
              onClick={handleCancelToggle}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* Field content */}
      {state.mode === "shared" ? (
        <textarea
          value={state.shared}
          onChange={(e) => onSharedChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
        />
      ) : (
        <div className="space-y-2">
          {members.map((member) => (
            <div key={member.studiomateMemberId}>
              <label className="block text-xs text-gray-500 mb-1">{member.memberName}</label>
              <textarea
                value={state.perMember[member.studiomateMemberId] ?? ""}
                onChange={(e) => onPerMemberChange(member.studiomateMemberId, e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
              />
              {renderPerMemberAddon?.(member.studiomateMemberId)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
